import { useMemo, useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import type { Token } from "@/types";
import { TokenAvatar, LazyVisible } from "./TokenAvatar";
import { CloseIcon, SearchIcon } from "./Icons";
import { TokenRowSkeleton } from "./Skeleton";
import { fmtNumber, fmtUsd, shortAddr } from "@/lib/utils";

interface TokenSelectDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tokens: Token[];
	isLoading: boolean;
	currentAddress: string;
	otherAddress?: string;
	onPick: (token: Token) => void;
}

export function TokenSelectDialog({
	open,
	onOpenChange,
	tokens,
	isLoading,
	currentAddress,
	otherAddress,
	onPick,
}: TokenSelectDialogProps) {
	const [query, setQuery] = useState("");

	useEffect(() => {
		if (!open) setQuery("");
	}, [open]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return tokens;
		return tokens.filter(
			(t) =>
				t.symbol.toLowerCase().includes(q) ||
				t.name.toLowerCase().includes(q) ||
				t.address.toLowerCase().includes(q),
		);
	}, [query, tokens]);

	const sorted = useMemo(
		() => [...filtered].sort((a, b) => b.balanceUsd - a.balanceUsd),
		[filtered],
	);

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="dialog-overlay" />
				<Dialog.Content className="dialog-content" aria-describedby={undefined}>
					<div className="dialog-head">
						<Dialog.Title className="dialog-title">Select a token</Dialog.Title>
						<Dialog.Close asChild>
							<button className="dialog-close" aria-label="Close">
								<CloseIcon />
							</button>
						</Dialog.Close>
					</div>

					<div className="token-search-wrap">
						<label className="token-search">
							<SearchIcon style={{ color: "var(--text-subtle)", flexShrink: 0 }} />
							<input
								autoFocus
								placeholder="Search by name, symbol, or address"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
							/>
						</label>
					</div>

					<div className="token-list">
						{isLoading ? (
							Array.from({ length: 6 }, (_, i) => <TokenRowSkeleton key={i} />)
						) : sorted.length === 0 ? (
							<div className="empty-state">No tokens match "{query}"</div>
						) : (
							sorted.map((t) => {
								const isSelected = t.address === currentAddress;
								const isOther = t.address === otherAddress;
								return (
									<LazyVisible key={t.address} height={57}>
										<button
											className={"token-row" + (isSelected ? " selected" : "")}
											onClick={() => {
												if (!isOther) onPick(t);
											}}
											disabled={isOther}
											style={isOther ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
											title={isOther ? "Already selected on the other side" : undefined}
										>
											<TokenAvatar token={t} size={36} />
											<div className="info">
												<div className="name">
													<span>{t.symbol}</span>
													<span
														style={{
															color: "var(--text-subtle)",
															fontWeight: 400,
															fontSize: 12,
														}}
													>
														{t.name}
													</span>
												</div>
												<div className="meta">
													Solana · {shortAddr(t.address)}
												</div>
											</div>
											<div className="right">
												{t.balance > 0 ? (
													<>
														<span className="price">{fmtUsd(t.balanceUsd)}</span>
														<span className="balance">
															{fmtNumber(t.balance, 4)} {t.symbol}
														</span>
													</>
												) : (
													<>
														<span className="price">{fmtUsd(t.usd)}</span>
														<span className="balance" style={{ color: "var(--text-subtle)" }}>
															0.00
														</span>
													</>
												)}
											</div>
										</button>
									</LazyVisible>
								);
							})
						)}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

export function PasteAddressDialog({
	open,
	onOpenChange,
	onConfirm,
	initial,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (addr: string) => void;
	initial: string;
}) {
	const [val, setVal] = useState(initial || "");
	useEffect(() => {
		if (open) setVal(initial || "");
	}, [open, initial]);

	const isValid = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(val.trim());

	const handlePaste = async () => {
		try {
			const text = await navigator.clipboard.readText();
			setVal(text.trim());
		} catch {
			/* clipboard blocked */
		}
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="dialog-overlay" />
				<Dialog.Content className="dialog-content" style={{ maxWidth: 420 }}>
					<div className="dialog-head">
						<Dialog.Title className="dialog-title">Paste recipient address</Dialog.Title>
						<Dialog.Close asChild>
							<button className="dialog-close" aria-label="Close">
								<CloseIcon />
							</button>
						</Dialog.Close>
					</div>

					<div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
						<label className="address-input">
							<textarea
								rows={3}
								placeholder="Paste a Solana wallet address"
								value={val}
								onChange={(e) => setVal(e.target.value)}
								autoFocus
							/>
							<button type="button" className="paste-btn" onClick={handlePaste}>
								Paste
							</button>
						</label>
						<p
							style={{
								margin: 0,
								fontSize: 12,
								color: val && !isValid ? "var(--danger)" : "var(--text-muted)",
							}}
						>
							{val && !isValid
								? "Address doesn't look like a valid Solana wallet."
								: "Solana wallet addresses are 32–44 base58 characters."}
						</p>
					</div>

					<div className="confirm-actions">
						<button className="btn-secondary" onClick={() => onOpenChange(false)}>
							Cancel
						</button>
						<button
							className="btn-confirm"
							disabled={!isValid}
							onClick={() => onConfirm(val.trim())}
						>
							Use this address
						</button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
